import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Activity, Loader2, Eye, EyeOff, ShieldCheck, BarChart3, Factory } from "lucide-react";
import { GoogleIcon } from "@/components/GoogleIcon";


export const Route = createFileRoute("/login")({
  component: LoginPage,
});

const VIDEO_SOURCES = [
  "https://videos.pexels.com/video-files/3045163/3045163-hd_1920_1080_24fps.mp4",
  "https://videos.pexels.com/video-files/2933375/2933375-hd_1920_1080_30fps.mp4",
  "https://assets.mixkit.co/videos/4787/4787-720.mp4",
];

function LoginPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<"login" | "signup">("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [fullName, setFullName] = useState("");
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [videoReady, setVideoReady] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate({ to: "/dashboard" });
  }, [user, loading, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: `${window.location.origin}/dashboard`,
            data: { full_name: fullName },
          },
        });
        if (error) throw error;
        toast.success("Cadastro criado! Aguarde aprovação do administrador.");
        setMode("login");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        navigate({ to: "/dashboard" });
      }
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleBusy(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/dashboard`,
        },
      });
      if (error) {
        toast.error(error.message ?? "Falha no login com Google");
        setGoogleBusy(false);
        return;
      }
      // O navegador é redirecionado ao Google automaticamente
    } catch (err) {
      toast.error((err as Error).message);
      setGoogleBusy(false);
    }
  };



  return (
    <div className="min-h-screen relative flex bg-[#0a0a0f] text-white overflow-hidden">
      {/* Vídeo de fundo */}
      <video
        autoPlay
        muted
        loop
        playsInline
        onCanPlay={() => setVideoReady(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${videoReady ? "opacity-60" : "opacity-0"}`}
      >
        {VIDEO_SOURCES.map((src) => (
          <source key={src} src={src} type="video/mp4" />
        ))}
      </video>

      {/* Camadas de overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#05060a]/95 via-[#05060a]/70 to-[#05060a]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#05060a] via-transparent to-[#05060a]/30" />
      <div
        className="absolute inset-0 opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
          backgroundSize: "56px 56px",
        }}
      />

      {/* Conteúdo */}
      <div className="relative z-10 flex flex-1 flex-col lg:flex-row">
        {/* Lado esquerdo – branding */}
        <div className="hidden lg:flex flex-1 flex-col justify-between p-12 xl:p-16">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-xl bg-white text-[#05060a] flex items-center justify-center shadow-xl">
              <Activity className="h-5 w-5" />
            </div>
            <div className="leading-tight">
              <div className="font-semibold text-[15px] tracking-tight">Apontamentos</div>
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-white/50">
                Plataforma de Análise Industrial
              </div>
            </div>
          </div>

          <div className="max-w-xl space-y-8 animate-fade-in">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/5 backdrop-blur text-[11px] uppercase tracking-wider text-white/80">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              Operação em tempo real
            </div>
            <h1 className="text-4xl xl:text-5xl font-semibold leading-[1.05] tracking-tight">
              Inteligência de chão de fábrica em um único painel.
            </h1>
            <p className="text-white/65 text-[15px] leading-relaxed max-w-md">
              Consolide apontamentos, identifique inconsistências e acompanhe a produtividade
              dos seus centros de trabalho com a precisão que a sua operação exige.
            </p>

            <div className="grid grid-cols-3 gap-4 pt-4 max-w-lg">
              {[
                { icon: Factory, label: "Centros monitorados" },
                { icon: BarChart3, label: "Análise contínua" },
                { icon: ShieldCheck, label: "Acesso controlado" },
              ].map((f) => (
                <div key={f.label} className="rounded-xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-4">
                  <f.icon className="h-4 w-4 text-white/70 mb-2" />
                  <div className="text-[11.5px] text-white/70 leading-tight">{f.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="text-[11px] text-white/40 tracking-wide">
            © {new Date().getFullYear()} Apontamentos · Análise Industrial
          </div>
        </div>

        {/* Lado direito – card */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-10">
          <div className="w-full max-w-md animate-fade-in">
            {/* Logo mobile */}
            <div className="lg:hidden flex items-center justify-center mb-8 gap-3">
              <div className="h-10 w-10 rounded-xl bg-white text-[#05060a] flex items-center justify-center">
                <Activity className="h-5 w-5" />
              </div>
              <div className="leading-tight">
                <div className="font-semibold text-[14px]">Apontamentos</div>
                <div className="text-[10px] uppercase tracking-wider text-white/50">
                  Análise Industrial
                </div>
              </div>
            </div>

            <div className="relative rounded-2xl border border-white/10 bg-[#0a0a0f]/85 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.6)] p-8">
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-white/10 via-transparent to-white/5 pointer-events-none" />
              <div className="relative">
                <div className="mb-6">
                  <h2 className="text-[22px] font-semibold tracking-tight">
                    {mode === "login" ? "Bem-vindo de volta" : "Crie sua conta"}
                  </h2>
                  <p className="text-[13px] text-white/55 mt-1">
                    {mode === "login"
                      ? "Acesse o painel da sua operação."
                      : "Cadastre-se e aguarde aprovação do administrador."}
                  </p>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-10 font-medium bg-white text-[#05060a] hover:bg-white/90 border-white/20"
                  onClick={handleGoogle}
                  disabled={googleBusy || busy}
                >
                  {googleBusy ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <GoogleIcon className="h-[18px] w-[18px] mr-1" />
                      Continuar com o Google
                    </>
                  )}
                </Button>

                <div className="my-5 flex items-center gap-3 text-[11px] text-white/40">
                  <div className="h-px flex-1 bg-white/10" />
                  <span className="uppercase tracking-wider">ou com e-mail</span>
                  <div className="h-px flex-1 bg-white/10" />
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">

                  {mode === "signup" && (
                    <div className="space-y-1.5">
                      <Label htmlFor="name" className="text-[12px] text-white/70">Nome completo</Label>
                      <Input
                        id="name"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        required
                        className="h-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-white/30"
                      />
                    </div>
                  )}
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-[12px] text-white/70">E-mail corporativo</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="voce@empresa.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-white/30"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="password" className="text-[12px] text-white/70">Senha</Label>
                    <div className="relative">
                      <Input
                        id="password"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        minLength={6}
                        required
                        className="h-10 pr-10 bg-white/5 border-white/10 text-white placeholder:text-white/30 focus-visible:ring-white/30"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 inline-flex items-center justify-center rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                        aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                        tabIndex={-1}
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button
                    type="submit"
                    className="w-full h-10 bg-white text-[#05060a] hover:bg-white/90 font-medium"
                    disabled={busy || googleBusy}
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === "login" ? "Entrar" : "Criar conta"}
                  </Button>
                </form>

                <div className="text-center mt-6 text-[12.5px] text-white/55">
                  {mode === "login" ? (
                    <>
                      Não tem conta?{" "}
                      <button className="text-white font-medium hover:underline" onClick={() => setMode("signup")}>
                        Cadastre-se
                      </button>
                    </>
                  ) : (
                    <>
                      Já tem conta?{" "}
                      <button className="text-white font-medium hover:underline" onClick={() => setMode("login")}>
                        Entrar
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="text-center mt-5 text-[11px] text-white/40">
              <Link to="/" className="hover:text-white/70 transition-colors">← Voltar ao início</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
